import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { CloudTerminalSessionInfo } from '../../services/cloud-terminal-service.js';

const MIN_WIDTH = 400;
const MAX_WIDTH_PERCENT = 0.75;
const STORAGE_KEY = 'cloud-terminal:sidebar-width';
const DEFAULT_WIDTH = 520;

@customElement('cp-cloud-terminal-sidebar')
export class CpCloudTerminalSidebar extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Boolean, reflect: true }) open = false;
  @property({ type: Array }) sessions: CloudTerminalSessionInfo[] = [];
  @property({ type: String }) activeSessionId = '';

  @state() private _width = DEFAULT_WIDTH;
  @state() private _dragging = false;

  connectedCallback(): void {
    super.connectedCallback();
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) this._width = Math.max(MIN_WIDTH, parseInt(saved, 10));
    } catch { /* ignore */ }
  }

  private _onClose(): void {
    this.dispatchEvent(new CustomEvent('sidebar-close', { bubbles: true, composed: true }));
  }

  private _onNewSession(): void {
    this.dispatchEvent(new CustomEvent('new-session', { bubbles: true, composed: true }));
  }

  private _onDragStart = (e: PointerEvent): void => {
    e.preventDefault();
    this._dragging = true;
    const startX = e.clientX;
    const startWidth = this._width;
    const maxWidth = window.innerWidth * MAX_WIDTH_PERCENT;

    const onMove = (me: PointerEvent) => {
      const delta = startX - me.clientX;
      this._width = Math.min(maxWidth, Math.max(MIN_WIDTH, startWidth + delta));
    };

    const onUp = () => {
      this._dragging = false;
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      try {
        localStorage.setItem(STORAGE_KEY, String(this._width));
      } catch { /* ignore */ }
    };

    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  };

  render() {
    const sidebarClass = `cp-cloud-terminal-sidebar${this.open ? ' cp-cloud-terminal-sidebar--open' : ''}${this._dragging ? ' cp-cloud-terminal-sidebar--dragging' : ''}`;
    const style = `width: ${this._width}px;`;

    return html`
      <div class="${sidebarClass}" style="${style}">
        <div
          class="cp-cloud-terminal-sidebar__drag-handle"
          @pointerdown=${this._onDragStart}
        ></div>

        <div class="cp-cloud-terminal-sidebar__content">
          <!-- Header -->
          <div class="cp-cloud-terminal-sidebar__header">
            <div class="cp-cloud-terminal-sidebar__title">
              <span>Cloud Terminal</span>
              ${this.sessions.length > 0 ? html`
                <span class="cp-cloud-terminal-sidebar__badge">${this.sessions.length}</span>
              ` : nothing}
            </div>
            <div class="cp-cloud-terminal-sidebar__actions">
              <button
                class="cp-cloud-terminal-sidebar__btn"
                @click=${this._onNewSession}
                title="Neue Session"
              >+</button>
              <button
                class="cp-cloud-terminal-sidebar__btn"
                @click=${this._onClose}
                title="Schliessen"
              >&times;</button>
            </div>
          </div>

          <!-- Tabs -->
          <cp-cloud-terminal-tabs
            .sessions=${this.sessions}
            .activeSessionId=${this.activeSessionId}
          ></cp-cloud-terminal-tabs>

          <!-- Session panels -->
          <div class="cp-cloud-terminal-sidebar__panels">
            ${this.sessions.length === 0 ? html`
              <div class="cp-cloud-terminal-sidebar__empty">
                <div class="cp-cloud-terminal-sidebar__empty-icon">&#9638;</div>
                <div class="cp-cloud-terminal-sidebar__empty-text">Keine aktiven Sessions</div>
                <button
                  class="cp-cloud-terminal-sidebar__empty-btn"
                  @click=${this._onNewSession}
                >Neue Session starten</button>
              </div>
            ` : nothing}

            ${this.sessions.map(session => html`
              <cp-cloud-terminal-session
                .session=${session}
                .active=${session.id === this.activeSessionId}
              ></cp-cloud-terminal-session>
            `)}
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-cloud-terminal-sidebar': CpCloudTerminalSidebar;
  }
}
